import { useEffect, useRef, useMemo, useCallback, SyntheticEvent } from "react"

import style from "modules/PsyProfile/components/AboutMe/components/AudioPlayer/style.module.scss"
import { isBrowser } from "utils/infrastructure/ssg-utils"

interface Props {
    onChange(value: number): void
    current?: number
    maxDuration?: number
}

const DEFAULT_CURRENT_TIME = "00:00"
const DEFAULT_DURATION = "--:--"

function calculateTime(secs: number) {
    const minutes = Math.floor(secs / 60)
    const seconds = Math.floor(secs % 60)
    const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`
    return `${minutes}:${returnedSeconds}`
}

export function to2DigitsFraction(value: number, maxValue: number) {
    return Math.round((value / maxValue) * 10000) / 10000
}

function getClientX(event: MouseEvent | TouchEvent) {
    try {
        if (event instanceof TouchEvent) {
            return event.touches[0].clientX
        }
    } catch {
        /* empty */
    }
    if (event instanceof MouseEvent) {
        return event.clientX
    }
    throw new Error(`Unknown event type: ${JSON.stringify(event)}`)
}

export function TrackSlider({ onChange, current, maxDuration }: Props) {
    const sliderValue =
        typeof current !== "undefined" && maxDuration ? to2DigitsFraction(current, maxDuration) : 0
    const sliderRef = useRef<HTMLDivElement>(null)
    const thumbRef = useRef<HTMLDivElement>(null)
    const updateSliderByMouseMove = useRef(false)
    const rootStyle = isBrowser() ? document.documentElement.style : null
    const playTracking = useMemo(
        () => ({
            disable() {
                updateSliderByMouseMove.current = true
            },
            enable() {
                updateSliderByMouseMove.current = false
            },
            isEnabled() {
                return !updateSliderByMouseMove.current
            },
        }),
        []
    )
    const dimensions = useMemo(
        () => ({
            getThumbBoundingClientRect() {
                const { current: thumb } = thumbRef
                if (!thumb) return
                return thumb.getBoundingClientRect()
            },
            getSliderBoundingClientRect() {
                const { current: slider } = sliderRef
                if (!slider) return
                return slider.getBoundingClientRect()
            },
            getThumbShiftX(clientX: number, shiftX: number) {
                const { current: slider } = sliderRef
                const { current: thumb } = thumbRef
                if (!slider || !thumb) return
                const rect = dimensions.getSliderBoundingClientRect()
                const sliderLeft = rect?.left
                const sliderWidth = rect?.width

                if (!sliderLeft || !sliderWidth) return

                let thumbShiftX = clientX - shiftX - sliderLeft

                if (thumbShiftX < 0) {
                    thumbShiftX = 0
                }
                const rightEdge = slider.offsetWidth - thumb.offsetWidth
                if (thumbShiftX > rightEdge) {
                    thumbShiftX = rightEdge
                }
                return thumbShiftX
            },
        }),
        []
    )
    const handlePointerEvent = useCallback(
        (event: SyntheticEvent<HTMLDivElement, MouseEvent | TouchEvent>) => {
            const { current: thumb } = thumbRef
            if (!thumb) return

            const valueTracker = {
                current: sliderValue,
                update(newValue: number) {
                    valueTracker.current = newValue
                },
                get lastValue() {
                    return valueTracker.current
                },
            }

            const thumbRect = dimensions.getThumbBoundingClientRect()
            const shiftX = getClientX(event.nativeEvent) - (thumbRect?.left ?? 0)

            function onPointerMove(event: MouseEvent | TouchEvent) {
                const sliderRect = dimensions.getSliderBoundingClientRect()
                const newLeft = dimensions.getThumbShiftX(getClientX(event), shiftX)
                if (newLeft && sliderRect && thumbRect && rootStyle) {
                    const value = to2DigitsFraction(newLeft, sliderRect.width - thumbRect.width)
                    rootStyle.setProperty("--alter-audio-slider-position", `${value * 100}%`)
                    updateDisplayedTime(value, maxDuration)
                    valueTracker.update(value)
                }
            }

            function onMouseUp() {
                document.removeEventListener("mousemove", onPointerMove)
                document.removeEventListener("mouseup", onMouseUp)
                document.removeEventListener("touchmove", onPointerMove)
                document.removeEventListener("touchend", onMouseUp)
                playTracking.enable()
                onChange(valueTracker.lastValue)
            }

            playTracking.disable()
            document.addEventListener("mousemove", onPointerMove)
            document.addEventListener("mouseup", onMouseUp)
            document.addEventListener("touchmove", onPointerMove)
            document.addEventListener("touchend", onMouseUp)
        },
        [maxDuration]
    )
    const {
        dragStartHandler,
        getCurrentTimeString,
        getRemainingDurationString,
        updateDisplayedTime,
    } = useMemo(
        () => ({
            dragStartHandler(this: void) {
                return false
            },
            updateDisplayedTime(this: void, value: number, maxDuration?: number) {
                const { current: slider } = sliderRef
                if (!slider) return
                if (!maxDuration) {
                    slider.dataset.current = getCurrentTimeString()
                    slider.dataset.remaining = getRemainingDurationString()
                    return
                }
                const current = value * maxDuration
                const remaining = maxDuration - current
                slider.dataset.current = getCurrentTimeString(current)
                slider.dataset.remaining = getRemainingDurationString(remaining)
            },
            getCurrentTimeString(this: void, current?: number) {
                return current ? calculateTime(current) : DEFAULT_CURRENT_TIME
            },
            getRemainingDurationString(this: void, remaining?: number) {
                if (typeof remaining !== "undefined") return `${calculateTime(remaining)}`
                if (maxDuration) return `${calculateTime(maxDuration)}`
                return DEFAULT_DURATION
            },
        }),
        []
    )
    const handleClick = useCallback(
        (event: SyntheticEvent<HTMLDivElement, MouseEvent>) => {
            event.preventDefault()
            if (event.target === thumbRef.current) return
            const clientX = event.nativeEvent.clientX
            const thumbRect = dimensions.getThumbBoundingClientRect()
            const sliderRect = dimensions.getSliderBoundingClientRect()
            if (sliderRect && thumbRect) {
                const newLeft = clientX - sliderRect.left
                onChange(to2DigitsFraction(newLeft, sliderRect.width - thumbRect.width))
            }
        },
        [maxDuration]
    )

    useEffect(() => {
        if (typeof sliderValue !== "undefined" && playTracking.isEnabled() && rootStyle) {
            rootStyle.setProperty("--alter-audio-slider-position", `${sliderValue * 100}%`)
            updateDisplayedTime(sliderValue, maxDuration)
        }
    }, [sliderValue])

    useEffect(() => {
        updateDisplayedTime(0, maxDuration)
    }, [maxDuration])

    return (
        <div
            ref={sliderRef}
            className={style.slider}
            data-current=""
            data-remaining=""
            onClick={handleClick}
        >
            <div className={style.touchZone} />
            <div
                ref={thumbRef}
                onMouseDown={handlePointerEvent}
                onTouchStart={handlePointerEvent}
                onDragStart={dragStartHandler}
                className={style.thumb}
            />
        </div>
    )
}
